import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestUtilizationEfficiencyReportsInterFace,
  UtilizationEfficiencyReportsInterFace,
} from '../../../../interface/utilization-efficiency-report-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchUtilizationEfficiencyReports,
  setSortData,
} from '../../../../redux/slices/UtilizationEfficiencyReportSlice';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import stationManagement from '../../../../utils/api/location-management';

interface ReportLeftTopContent {
  leftTopContent?: JSX.Element;
}
/**
 * UtilizationEfficiencyReport
 * @param {any} properties - The properties
 * @returns {any} The rendered component
 */
function UtilizationEfficiencyReport(properties: ReportLeftTopContent) {
  const { t } = useTranslation('sidePanel');
  const { leftTopContent } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const currentDate = new Date();
  const yesterday = new Date(currentDate);
  yesterday.setDate(currentDate.getDate() - 1);
  /**
   * Filter date format func
   * @param {Date} date - The date to format.
   * @returns {string} - The formatted date string.
   */
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const { startDate, endDate } = dateRange;
  const {
    UtilizationEfficiencyReports,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.UtilizationEfficiencyReport);
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const column = [
    {
      label: t('deviceSnShort'),
      accessorKey: 'chargingPointSn' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('utilizationPercentage'),
      accessorKey: 'utilizationPercentage' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
      /**
       * customData
       * @param {UtilizationEfficiencyReportsInterFace} data - The data
       * @returns {any} - The rendered element
       */
      customData: (data: UtilizationEfficiencyReportsInterFace) => (
        <p>{data.utilizationPercentage || 0}</p>
      ),
    },
    {
      label: t('idleTimeRatio'),
      accessorKey: 'idleTimeRatio' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
      /**
       * customData
       * @param {UtilizationEfficiencyReportsInterFace} data - The data
       * @returns {any} - The rendered element
       */
      customData: (data: UtilizationEfficiencyReportsInterFace) => (
        <p>{(data as any).idleTimeRatio || 0}</p>
      ),
    },
    // {
    //   label: t('peakVsOffPeakSessionRatio'),
    //   accessorKey: 'offPeakSessions' as keyof UtilizationEfficiencyReportsInterFace,
    //   sortAllow: true,
    //   /**
    //    * peakSessions
    //    * @param {any} UtilizationEfficiency - The UtilizationEfficiency
    //    */
    //   customData: (UtilizationEfficiency: UtilizationEfficiencyReportsInterFace) => (
    //     <p>
    //       {UtilizationEfficiency.peakSessions}-{UtilizationEfficiency.offPeakSessions}
    //     </p>
    //   ),
    // },
    // {
    //   label: t('sessionsPerConnector'),
    //   accessorKey: 'totalSessionsPerConnector' as keyof UtilizationEfficiencyReportsInterFace,
    //   sortAllow: true,
    // },
    // {
    //   label: t('revenuePerKwh'),
    //   accessorKey: 'totalRevenuePerConnector' as keyof UtilizationEfficiencyReportsInterFace,
    //   sortAllow: true,
    // },
  ];

  /**
   * Handle the fetch the data in api function
   * @param {any} data The request data
   */
  const fetchData = (data: RequestUtilizationEfficiencyReportsInterFace) => {
    if (!loading && selectedChargingPoint?.id) {
      dispatch(
        fetchUtilizationEfficiencyReports({ ...data, chargingStation: selectedChargingPoint.id }),
      );
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('deviceSnShort'), accessorKey: 'chargingPointSn' },
    { label: t('utilizationPercentage'), accessorKey: 'utilizationPercentage' },
    { label: t('idleTimeRatio'), accessorKey: 'idleTime' },
    { label: t('peakVsOffPeakSessionRatio'), accessorKey: '__peakOffPeak__' },
    { label: t('sessionsPerConnector'), accessorKey: 'totalSessionsPerConnector' },
    { label: t('revenuePerKwh'), accessorKey: 'totalRevenuePerConnector' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    stationManagement
      .fetchReportUtilizationExport({
        startDate,
        endDate,
        chargingStation: selectedChargingPoint?.id,
        sortBy,
        sortField,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map(
          (row: UtilizationEfficiencyReportsInterFace, index: number) =>
            exportCurrentColumns.map((col) => {
              switch (col.accessorKey) {
                case '__serial__':
                  return index + 1;
                case 'created_at':
                  return ConvertUTCtoDate(row?.created_at) || t('notAvailable');
                case 'time':
                  return ConvertUTCtoHHMM(row?.created_at) || t('unavailable');
                default:
                  return (row as any)[col?.accessorKey] || t('unavailable');
              }
            }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-location-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-location-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    dispatch(setSortData(sortData));
    fetchData({
      startDate,
      endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    fetchData({
      ...value,
      startDate,
      endDate,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    fetchData({ startDate, endDate, page, limit, sortBy, sortField });
  };

  /**
   * Function to auto refresh data
   */
  const handleAutoRefresh = () => {
    fetchData({ startDate, endDate, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    fetchData({
      page: DefaultApiLimit.page,
      limit,
      sortBy,
      sortField,
      startDate,
      endDate,
    });
  }, [selectedChargingPoint?.id, startDate, endDate]);
  return (
    <div>
      <CustomTable
        version="v2"
        header={column}
        data={UtilizationEfficiencyReports}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        leftTopContent={leftTopContent}
        pageLimitChange={pageNateLimitChange}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        height={400}
        disableHeader={false}
        rightTopContent={
          <div className="reports-date-picker">
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
              placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
              defaultPreset="last14Days"
            />
          </div>
        }
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default UtilizationEfficiencyReport;
